// NOTE : NumberDoc corresponds to Number entity
// But because it clashes with the Number type, it is named NumberDoc
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { EPhonePlatform, EChannel, ENumberProducts } from '@leedpulse-monorepo/enums';

export default class NumberDoc {
  private _id: string;
  private _groupId: string;
  private _groupName: string;
  private _provider: EPhonePlatform;
  private _sender: string;
  private _channel: EChannel;
  private _country: string;
  private _created: number;
  private _notes: string;
  private _available: boolean;
  private _redirection: string;
  private _product: ENumberProducts;

  constructor(
    id: string,
    groupId: string,
    groupName: string,
    provider: EPhonePlatform,
    sender: string,
    channel: EChannel,
    country: string,
    notes: string,
    created: number,
    available: boolean,
    redirection: string,
    product: ENumberProducts
  ) {
    this._id = id;
    this._groupId = groupId;
    this._groupName = groupName;
    this._provider = provider;
    this._sender = sender;
    this._channel = channel;
    this._country = country;
    this._notes = notes;
    this._created = created;
    this._available = available;
    this._redirection = redirection;
    this._product = product;
  }

  get id(): string {
    return this._id;
  }

  get groupId(): string {
    return this._groupId;
  }

  get groupName(): string {
    return this._groupName;
  }

  get provider(): EPhonePlatform {
    return this._provider;
  }

  get sender(): string {
    return this._sender;
  }

  get channel(): EChannel {
    return this._channel;
  }

  get country(): string {
    return this._country;
  }

  get notes(): string {
    return this._notes;
  }

  get available(): boolean {
    return this._available;
  }

  get redirection(): string {
    return this._redirection;
  }

  get product(): ENumberProducts {
    return this._product;
  }

  get created(): number {
    return this._created;
  }

  /**
   * Returns formatted phone number for display
   * Example: "33612345678" -> "+33 6 12 34 56 78"
   * Example: "+33612345678" -> "+33 6 12 34 56 78"
   */
  get formattedNumber(): string {
    let num = this._id;

    // Remove + if present for consistent handling
    if (num.startsWith('+')) {
      num = num.slice(1);
    }

    // French formatting: +33 X XX XX XX XX
    if (num.startsWith('33') && num.length === 11) {
      return `+33 ${num.slice(2, 3)} ${num.slice(3, 5)} ${num.slice(5, 7)} ${num.slice(
        7,
        9
      )} ${num.slice(9, 11)}`;
    }

    // German formatting: +49 XXX XXXXXXXX
    if (num.startsWith('49') && num.length >= 12) {
      return `+49 ${num.slice(2, 5)} ${num.slice(5)}`;
    }

    // Swiss formatting: +41 XX XXX XX XX
    if (num.startsWith('41') && num.length === 11) {
      return `+41 ${num.slice(2, 4)} ${num.slice(4, 7)} ${num.slice(7, 9)} ${num.slice(9, 11)}`;
    }

    // UK formatting: +44 XXXX XXXXXX
    if (num.startsWith('44') && num.length === 12) {
      return `+44 ${num.slice(2, 6)} ${num.slice(6)}`;
    }

    // Default: add + prefix
    return `+${num}`;
  }

  static fromFirestore(data: QueryDocumentSnapshot<DocumentData, DocumentData>): NumberDoc {
    const numberData = data.data();
    return new NumberDoc(
      data.id,
      numberData.group_id ?? '',
      numberData.group_name ?? '',
      numberData.provider ?? EPhonePlatform.twilio,
      numberData.sender ?? '',
      numberData.channel ?? EChannel.sms,
      numberData.country ?? 'france',
      numberData.notes ?? '',
      numberData.created ?? new Date().getTime(),
      numberData.available ?? true,
      numberData.redirection ?? '',
      numberData.product ?? ENumberProducts.all
    );
  }
}
